// Tiny fetch wrapper for /public/client/<slug>/* endpoints. We hand-roll
// this instead of pulling a heavier client — the widget only needs a few
// GETs and POSTs and the response shapes are stable.
//
// All errors surface as plain Error objects with a `.status` field the
// React tree can read directly.

// In dev VITE_API_ORIGIN is unset — BASE stays '' so relative URLs hit the
// Vite proxy which forwards /public/* to localhost:3000.
// Production uses the Cloudflare Worker, matching the dashboard and landing.
// VITE_API_ORIGIN can override it for preview environments.
const BASE = import.meta.env.VITE_API_ORIGIN ||
  (import.meta.env.PROD ? 'https://proxy-api.sanookpass.com' : '');

export const APIError = class APIError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body;
  }
};

async function request(method, path, body, options = {}) {
  const url = `${BASE}${path}`;
  const headers = {
    ...(body ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers || {}),
  };
  const res = await fetch(url, {
    method,
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body ? JSON.stringify(body) : undefined,
    credentials: 'omit',
    mode: 'cors',
  });
  const text = await res.text();
  let parsed = null;
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }
  if (!res.ok) {
    const name = parsed?.error?.name ?? `HTTP_${res.status}`;
    throw new APIError(name, res.status, parsed);
  }
  return parsed;
}

export const api = {
  // Read paths
  profile: (slug) => request('GET', `/public/client/${slug}/profile`),
  services: (slug) => request('GET', `/public/client/${slug}/services`),
  staff: (slug) => request('GET', `/public/client/${slug}/staff`),
  schedule: (slug, query = {}) => {
    const qs = new URLSearchParams(query).toString();
    return request('GET', `/public/client/${slug}/schedule${qs ? `?${qs}` : ''}`);
  },
  contact: (slug, lang = 'en') =>
    request('GET', `/public/client/${slug}/contact?lang=${lang}`),
  refundPolicy: (slug, lang = 'en') =>
    request('GET', `/public/client/${slug}/refund-policy?lang=${lang}`),
  booking: (slug, id, reservationToken) =>
    request('GET', `/public/client/${slug}/booking/${id}`, null, {
      headers: { 'X-Booking-Token': reservationToken },
    }),

  // Write paths
  createBooking: (slug, body) =>
    request('POST', `/public/client/${slug}/booking`, body),
  resumePayment: (slug, id, reservationToken) =>
    request('POST', `/public/client/${slug}/booking/${id}/payment`, {}, {
      headers: { 'X-Booking-Token': reservationToken },
    }),
  submitOmisePayment: (slug, id, reservationToken, body) =>
    request('POST', `/public/client/${slug}/booking/${id}/payment`, body, {
      headers: { 'X-Booking-Token': reservationToken },
    }),
  cancelUnpaidPayment: (slug, id, reservationToken) =>
    request('POST', `/public/client/${slug}/booking/${id}/payment/cancel`, {}, {
      headers: { 'X-Booking-Token': reservationToken },
    }),
  cancelBooking: (slug, id, body) =>
    request('POST', `/public/client/${slug}/booking/${id}/cancel`, body),
};

export default api;
